/**
 * @fileoverview Error Handling Utilities
 * @description Standardized error parsing and user-friendly message generation
 *
 * Features:
 * - Parse API errors into user-friendly messages
 * - Handle network errors gracefully
 * - Extract error details from various error formats
 * - Support for FastAPI error responses
 *
 * @example
 * import { getErrorMessage } from '@/utils/errorHandling'
 *
 * try {
 *   await api.createLab(data)
 * } catch (error) {
 *   const message = getErrorMessage(error)
 *   toast.error(message)
 * }
 */

/**
 * Parse API error into user-friendly message
 *
 * @param {Error|Object} error - The error object from API call
 * @returns {string} User-friendly error message
 *
 * @example
 * // Network error
 * getErrorMessage({ code: 'ERR_NETWORK' })
 * // => "Network error. Please check your connection."
 *
 * // API error with detail
 * getErrorMessage({ response: { status: 404, data: { detail: 'Lab not found' } } })
 * // => "Lab not found"
 *
 * // Timeout
 * getErrorMessage({ code: 'ECONNABORTED' })
 * // => "Request timed out. Please try again."
 */
export function getErrorMessage(error) {
  if (!error) {
    return 'An unknown error occurred'
  }

  // Network errors (no response from server)
  if (error.code === 'ERR_NETWORK') {
    return 'Network error. Please check your internet connection and try again.'
  }

  // Request timeout
  if (error.code === 'ECONNABORTED') {
    return 'Request timed out. Please try again.'
  }

  // API errors with response
  if (error.response) {
    const { status, data } = error.response

    // FastAPI validation errors
    if (status === 422 && data?.detail) {
      // Handle Pydantic validation errors
      if (Array.isArray(data.detail)) {
        const messages = data.detail.map(err => {
          const field = err.loc?.slice(1).join('.') || 'field'
          return `${field}: ${err.msg}`
        })
        return messages.join('; ')
      }
      return data.detail
    }

    // Extract error message from response
    if (data?.detail) {
      return data.detail
    }

    if (data?.message) {
      return data.message
    }

    // HTTP status code based messages
    switch (status) {
      case 400:
        return 'Invalid request. Please check your input and try again.'
      case 401:
        return 'Your session has expired. Please log in again.'
      case 403:
        return 'You do not have permission to perform this action.'
      case 404:
        return 'The requested resource was not found.'
      case 409:
        return 'This action conflicts with existing data. Please refresh and try again.'
      case 429:
        return 'Too many requests. Please wait a moment and try again.'
      case 500:
        return 'Server error. Please try again later.'
      case 502:
        return 'Bad gateway. The server is temporarily unavailable.'
      case 503:
        return 'Service unavailable. Please try again later.'
      case 504:
        return 'Gateway timeout. Please try again.'
      default:
        if (status >= 500) {
          return 'Server error. Please try again later.'
        }
        if (status >= 400) {
          return 'Request failed. Please check your input and try again.'
        }
    }
  }

  // Generic error with message
  if (error.message) {
    return error.message
  }

  // Fallback
  return 'An unexpected error occurred. Please try again.'
}

/**
 * Extract error details for logging/debugging
 *
 * @param {Error|Object} error - The error object
 * @returns {Object} Error details
 * @property {string} message - Error message
 * @property {number|null} status - HTTP status code
 * @property {string|null} code - Error code
 * @property {Object|null} data - Additional error data
 *
 * @example
 * const details = getErrorDetails(error)
 * console.error('API Error:', details)
 */
export function getErrorDetails(error) {
  return {
    message: getErrorMessage(error),
    status: error.response?.status || null,
    code: error.code || null,
    data: error.response?.data || null,
    stack: error.stack || null
  }
}

/**
 * Check if error is a network/connection error
 *
 * @param {Error|Object} error - The error object
 * @returns {boolean} True if network error
 *
 * @example
 * if (isNetworkError(error)) {
 *   toast.error('No internet connection')
 * }
 */
export function isNetworkError(error) {
  return error?.code === 'ERR_NETWORK' || error?.code === 'ECONNABORTED' || !error?.response
}

/**
 * Check if error is an authentication error
 *
 * @param {Error|Object} error - The error object
 * @returns {boolean} True if auth error
 *
 * @example
 * if (isAuthError(error)) {
 *   // Router guard will handle redirect to Okta login
 *   router.push('/dashboard')
 * }
 */
export function isAuthError(error) {
  return error?.response?.status === 401
}

/**
 * Check if error is a permission error
 *
 * @param {Error|Object} error - The error object
 * @returns {boolean} True if permission error
 *
 * @example
 * if (isPermissionError(error)) {
 *   toast.error('Access denied')
 * }
 */
export function isPermissionError(error) {
  return error?.response?.status === 403
}

/**
 * Check if error is a validation error
 *
 * @param {Error|Object} error - The error object
 * @returns {boolean} True if validation error
 *
 * @example
 * if (isValidationError(error)) {
 *   // Show field-level errors
 * }
 */
export function isValidationError(error) {
  return error?.response?.status === 422
}

/**
 * Check if error is retryable (5xx server errors or network errors)
 *
 * @param {Error|Object} error - The error object
 * @returns {boolean} True if error is retryable
 *
 * @example
 * if (isRetryableError(error)) {
 *   await retryRequest()
 * }
 */
export function isRetryableError(error) {
  const status = error?.response?.status
  return isNetworkError(error) || (status >= 500 && status < 600)
}
